import {type ReactNode} from 'react';
import {useQuery} from '@tanstack/react-query';
import {apiCall} from '@/api-transport/client';
import {GC_TIME, STALE_TIME} from '@/api-transport/constants';
import {useAuthStatus} from '@/auth/hooks';
import {FeaturesContext} from './FeaturesContext';
import type {FeaturesContextValue} from './types';

type UserFeaturesProviderProps = {
  children: ReactNode;
};

type FeatureToggleRow = {
  name: string;
  isEnabled: boolean;
};

type FeatureTogglesResponse = {
  rows: FeatureToggleRow[];
  total: number;
};

export function UserFeaturesProvider({children}: UserFeaturesProviderProps) {
  const status = useAuthStatus();

  const {data, isLoading} = useQuery({
    queryKey: ['featureToggles'] as const,
    queryFn: () =>
      apiCall<FeatureTogglesResponse>('/private/getFeatureToggles', {
        pagination: [0, 20],
        sort: {column: 'name', direction: 'ASC'},
      }),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: status === 'authenticated',
  });

  const features: string[] = (data?.rows ?? [])
    .filter((row) => row.isEnabled)
    .map((row) => row.name);

  const hasFeature = (featureName: string): boolean =>
    features.includes(featureName);

  const value: FeaturesContextValue = {features, hasFeature, isLoading};

  return (
    <FeaturesContext.Provider value={value}>
      {children}
    </FeaturesContext.Provider>
  );
}
